import {
  Component,
  effect,
  inject,
  input,
  InputSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ETicketType, ISortGroupWithTicketData } from '@web/dtos';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { ESortOrder } from '../table/table.component';
import { NgClass } from '@angular/common';
import {
  EDropListViewMode,
  TicketDragAndDropSupportService,
} from '../../../services/support/ticket-drag-and-drop-support/ticket-drag-and-drop-support.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { CmsTicketCategoryService } from '../../../services/cms-ticket-category/cms-ticket-category.service';

@Component({
  selector: 'aw-unassigned-ticket-drag-and-drop-list',
  imports: [
    ReactiveFormsModule,
    CdkDropList,
    CdkDrag,
    AppendTestIdPipe,
    NgClass,
    CdkDragPreview,
    CdkDragPlaceholder,
    CdkScrollable,
  ],
  templateUrl: './unassigned-tickets-drag-and-drop-list.component.html',
  styles: ``,
})
export class UnassignedTicketsDragAndDropListComponent {
  public isDisabled: InputSignal<boolean> = input.required();
  public ticketType: InputSignal<ETicketType> = input.required();
  public searchControl: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
  });
  public readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly cmsTicketCategoryService: CmsTicketCategoryService =
    inject(CmsTicketCategoryService);
  protected ticketDragAndDropSupportService: TicketDragAndDropSupportService =
    inject(TicketDragAndDropSupportService);
  sortState: ESortOrder | undefined = undefined;

  protected readonly SortOrder: typeof ESortOrder = ESortOrder;
  public tickets: ISortGroupWithTicketData[] = [];
  public visibleTickets: ISortGroupWithTicketData[] = [];
  public isBeingDragged: WritableSignal<boolean> = signal(false);
  protected readonly EDropListViewMode: typeof EDropListViewMode =
    EDropListViewMode;
  public previewVisible: WritableSignal<boolean> = signal(false);

  constructor() {
    effect((): void => {
      if (this.ticketType() === ETicketType.Direct) {
        this.tickets =
          this.cmsTicketCategoryService.unassignedFixedPriceTicketGroups();
      }
      if (this.ticketType() === ETicketType.Relation) {
        this.tickets =
          this.cmsTicketCategoryService.unassignedRelationTicketGroups();
      }
      this.updateVisibleTickets();
    });

    effect((): void => {
      this.isBeingDragged.set(
        this.ticketDragAndDropSupportService.ticketIsBeingDragged()
      );
    });

    this.searchControl.valueChanges.subscribe(() => {
      this.updateVisibleTickets();
    });
  }

  public toggleSort(): void {
    if (this.sortState === undefined) {
      this.sortState = ESortOrder.ASC;
    } else if (this.sortState === ESortOrder.ASC) {
      this.sortState = ESortOrder.DESC;
    } else {
      this.sortState = undefined;
    }
    this.updateVisibleTickets();
  }

  private updateVisibleTickets(): void {
    const search: string = this.searchControl.value.trim().toLowerCase();

    let visibleTickets: ISortGroupWithTicketData[] = this.tickets.filter(
      (ticket: ISortGroupWithTicketData): boolean =>
        ticket.name.toLowerCase().includes(search)
    );

    if (this.sortState === ESortOrder.ASC) {
      visibleTickets = [...visibleTickets].sort((a, b) =>
        a.name.localeCompare(b.name, 'de')
      );
    } else if (this.sortState === ESortOrder.DESC) {
      visibleTickets = [...visibleTickets].sort((a, b) =>
        b.name.localeCompare(a.name, 'de')
      );
    }

    this.visibleTickets = visibleTickets;
  }

  public toggleTicketDragSignal(value: boolean): void {
    this.ticketDragAndDropSupportService.ticketIsBeingDragged.set(value);
  }

  public dropTicket(event: CdkDragDrop<ISortGroupWithTicketData[]>): void {
    const ticketGroup: ISortGroupWithTicketData = event.item.data;

    if (
      !event.isPointerOverContainer ||
      !ticketGroup?.ticketData?.ticketCategoryId
    ) {
      return;
    }

    this.cmsTicketCategoryService.removeTicketFromCategory(ticketGroup);
  }

  public changePlaceholder(viewMode: EDropListViewMode) {
    this.ticketDragAndDropSupportService.currentDropListViewMode.set(viewMode);
  }
}
